import { useId } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { ButtonUI, FieldEdition, LoadingSkeleton, showNotification, useQueryFetch } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import ShareReport from '@/components/common/ShareReport/ShareReport';
import { formatMoney } from '@/components/driveToStore/common/helpers/formatters';
import AggregatedTiles from '@/components/driveToStore/common/ReportHeader/AggregatedTiles/AggregatedTiles';
import { isSharedReportSession } from '@/helpers/shareReport/shareReport.helper';
import { getFootfallReportName, updateFootfallReportName } from '@/services/footfall.service';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';
import { getSession } from '@/store/global/session/selectors';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors.ts';
import { Poi } from '@/types/footfall';

import type { AggregatedFootfallLineItemDataResponse, FootfallReportNameResponse } from '@/services/footfall.service';

import './reportHeader.scss';

type Props = {
  onAllReports?: () => void;
  lineItemIds: string[];
  lineItem?: AggregatedFootfallLineItemDataResponse['lineItemData'];
  pois: Poi[];
  sharedReportFullAccess?: boolean;
  variant: 'aggregated' | 'single';
  entityType: string;
  entityIds: string[];
  poiIds?: string[];
};

function formatDate(dateStr: string): { monthDay: string; year: string } {
  const date = new Date(dateStr);
  const formattedDate = date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  const [monthDay, year] = formattedDate.split(',').map(part => part.trim());

  return { monthDay, year };
}

export default function ReportHeader({
  entityIds,
  entityType,
  lineItem,
  lineItemIds,
  onAllReports,
  pois = [],
  poiIds,
  sharedReportFullAccess,
  variant
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const baseCurrency = useGlobalStore(getBaseCurrency);
  const user = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const session = useGlobalStore(getSession);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const backButtonId = useId();
  const downloadButtonId = useId();
  const allReportsButtonId = useId();
  const menuButtonId = useId();
  const nameEditionId = useId();

  const startDate = lineItem ? formatDate(lineItem.startDate) : null;
  const endDate = lineItem ? formatDate(lineItem.endDate) : null;
  const isMultiLineItem = lineItemIds.length > 1;
  const isScopeResolved = variant === 'aggregated' || !!poiIds;
  const hasShareReportsPermission =
    checkUserResourcePermissions('reportingAdvancedShareLink', user.toJSON(), userRoles) &&
    !isSharedReportSession(session);

  const defaultTitle =
    variant === 'aggregated'
      ? isMultiLineItem
        ? formatMessage({
            defaultMessage: 'Line Item aggregated report',
            id: 'driveToStore.aggregatedReport.header.multiTitle'
          })
        : formatMessage({ defaultMessage: 'Aggregated Report', id: 'driveToStore.aggregatedReport.header.title' })
      : formatMessage({ defaultMessage: 'Single Report', id: 'driveToStore.singleReport.header.title' });

  const entityIdParam = entityIds.join(',');
  const reportNameQueryKey = [
    'footfall-report-name',
    entityType,
    entityIdParam,
    poiIds ? [...poiIds].sort((a, b) => a.localeCompare(b)).join(',') : ''
  ];

  const { data: reportNameData } = useQueryFetch<FootfallReportNameResponse>(reportNameQueryKey, async () =>
    getFootfallReportName(entityType, entityIdParam, poiIds)
  );

  const displayName = reportNameData?.name ?? defaultTitle;

  const updateNameMutation = useMutation({
    mutationFn: (newName: string) => updateFootfallReportName(entityType, entityIdParam, poiIds, newName),
    onError: () => {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: "The report name couldn't be updated, please try again",
          id: 'driveToStore.reportHeader.name.updateError'
        })
      );
    },
    onSuccess: result => {
      queryClient.setQueryData(reportNameQueryKey, { name: result.name });
    }
  });

  return (
    <div className="reportHeader">
      <aside className="reportHeader__leftSection">
        {!isSharedReportSession(session) && (
          <ButtonUI
            className="subHeader__backButton"
            icon="fal fa-arrow-left"
            iconPosition="left"
            id={backButtonId}
            onClick={() => navigate(-1)}
            shape="squareRounded"
            size="small"
            type="secondary"
          />
        )}
        {!isScopeResolved ? (
          <LoadingSkeleton height={20} width={160} />
        ) : isSharedReportSession(session) ? (
          <span className="reportHeader__leftSectionTitle" title={displayName}>
            {displayName}
          </span>
        ) : (
          <FieldEdition
            className="reportHeader__nameEdition"
            disabled={updateNameMutation.isLoading}
            id={nameEditionId}
            onChange={newName => updateNameMutation.mutate(newName)}
            title={displayName}
            value={displayName}
          />
        )}
      </aside>
      {lineItem && (
        <>
          {isMultiLineItem ? (
            <div className="reportHeader__centerSection reportHeader__centerSection--multi">
              <span aria-hidden="true" className="reportHeader__divider" />
              <AggregatedTiles
                advertiserCount={lineItem.advertiserCount}
                baseCurrency={baseCurrency}
                budget={lineItem.budget}
                campaignCount={lineItem.campaignCount}
                lineItemCount={lineItem.lineItemCount}
                pricingTypesCount={lineItem.pricingTypesCount}
              />
            </div>
          ) : (
            <div className="reportHeader__centerSection">
              <div className="reportHeader__entityDetailSection">
                <span className="reportHeader__entityDetailSectionLabel">
                  {formatMessage({
                    defaultMessage: 'Line Item -',
                    id: 'driveToStore.aggregatedReport.header.lineItemLabel'
                  })}
                </span>
                <span className="reportHeader__entityDetailName" title={lineItem.name}>
                  {lineItem.name}
                </span>
              </div>
              <div className="reportHeader__entityDetailSection">
                <span className="reportHeader__entityDetailSectionLabel">
                  {formatMessage({
                    defaultMessage: 'Budget -',
                    id: 'driveToStore.aggregatedReport.header.budgetLabel'
                  })}
                </span>
                <span className="reportHeader__entityDetailBudget">{formatMoney(lineItem.budget, baseCurrency)}</span>
              </div>
              <div className="reportHeader__entityDetailSection">
                <span className="reportHeader__entityDetailSectionLabel">
                  {formatMessage({ defaultMessage: 'Dates -', id: 'driveToStore.aggregatedReport.header.datesLabel' })}
                </span>
                <span className="reportHeader__entityDetailDates">
                  <span className="reportHeader__entityDetailDatesDate">
                    <b>{startDate?.monthDay}</b>
                    {`, ${startDate?.year} `}
                  </span>
                  <span className="reportHeader__entityDetailDatesTo">
                    {formatMessage({ defaultMessage: 'to', id: 'driveToStore.aggregatedReport.header.to' })}
                  </span>
                  <span className="reportHeader__entityDetailDatesDate">
                    <b>{endDate?.monthDay}</b>
                    {`, ${endDate?.year}`}
                  </span>
                </span>
              </div>
            </div>
          )}
          <div className="reportHeader__rightSection">
            <div className="reportHeader__actions">
              <span style={{ display: 'none' }}>
                <ButtonUI
                  disabled
                  icon="fas fa-download"
                  iconPosition="right"
                  id={downloadButtonId}
                  shape="squareRounded"
                  size="small"
                  text={formatMessage({
                    defaultMessage: 'Download Report',
                    id: 'driveToStore.aggregatedReport.header.downloadReport'
                  })}
                  type="primary"
                />
              </span>
              {(!isSharedReportSession(session) || sharedReportFullAccess) && (
                <ButtonUI
                  disabled={!onAllReports}
                  icon="fas fa-chart-mixed"
                  iconPosition="right"
                  id={allReportsButtonId}
                  onClick={onAllReports}
                  shape="squareRounded"
                  size="small"
                  text={formatMessage({
                    defaultMessage: 'All reports',
                    id: 'driveToStore.aggregatedReport.header.allReports'
                  })}
                  type="tertiary"
                />
              )}
              {hasShareReportsPermission && (
                <ShareReport
                  buttonProps={{
                    icon: 'fas fa-share-nodes',
                    iconPosition: 'right',
                    id: 'shareReportButton',
                    shape: 'squareRounded',
                    size: 'small',
                    text: formatMessage({
                      defaultMessage: 'Share',
                      id:
                        variant === 'aggregated'
                          ? 'driveToStore.aggregatedReport.header.share'
                          : 'driveToStore.singleReport.header.share'
                    }),
                    type: 'tertiary'
                  }}
                  lineItemIds={lineItemIds}
                  pois={pois.map(poi => parseInt(poi.id))}
                  type={variant === 'aggregated' ? 'agregatedDriveToStore' : 'singleDriveToStore'}
                />
              )}
              <span style={{ display: 'none' }}>
                <ButtonUI
                  className="reportHeader__menuButton"
                  disabled
                  icon="fas fa-ellipsis-vertical"
                  id={menuButtonId}
                  shape="squareRounded"
                  size="small"
                  type="secondary"
                />
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
